import type { RefObject } from "react";
import classNames from "classnames";
import { noop } from "@common/helpers/function";
import { TriangleIcon } from "@icons";
import { ActiveFilterButton } from "@components/pageHeader/filters/ActiveFilters/ActiveFilterButton";
import { ActiveFilters } from "@components/pageHeader/filters/ActiveFilters/ActiveFilters";
import { ClearFiltersButton } from "@components/pageHeader/filters/ActiveFilters/ClearFiltersButton";
import { AggregatedFilters } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters";
import { useAggregatedFiltersContext } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters.context";
import { AggregatedFiltersOverlay } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersOverlay";
import { AggregatedFiltersProvider } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersProvider";
import { FilterNavTab } from "@components/pageHeader/filters/FilterNavTab";
import { FilterTabPanel } from "@components/pageHeader/filters/FilterTabPanel";
import overlayStyles from "@components/pageHeader/filters/FiltersOverlay.module.scss";
import type { ActivityFilterTab } from "./activityFilters.types";
import styles from "./ActivityFilters.module.scss";

interface ActivityFiltersProps {
    tabs: readonly ActivityFilterTab[];
    /** Shows Clear filters after the chips. */
    onClearFilters?: () => void;
    /** The toolbar's left side, whose width sets how many chips show before "+N more". */
    filtersContainerRef?: RefObject<HTMLDivElement | null>;
}

const ActivityFilterChip = ({ tab }: { tab: ActivityFilterTab }) => {
    const { open } = useAggregatedFiltersContext();
    return (
        <ActiveFilterButton
            label={tab.chipLabel}
            openFilter={() => open(tab.key)}
            resetFilter={tab.onClear}
        />
    );
};

/** An Activity page's Filter button with its tabbed popover, then a chip per set filter. */
export const ActivityFilters = ({
    tabs,
    onClearFilters,
    filtersContainerRef,
}: ActivityFiltersProps) => {
    const tabsByKey = new Map(tabs.map((tab) => [tab.key, tab]));
    const activeKeys = tabs.flatMap((tab) =>
        tab.chipLabel === undefined ? [] : [tab.key],
    );

    return (
        <AggregatedFiltersProvider defaultTab={tabs[0]?.key ?? ""}>
            <AggregatedFilters>
                <AggregatedFiltersOverlay
                    navigation={tabs.map((tab) => (
                        <FilterNavTab
                            key={tab.key}
                            eventKey={tab.key}
                            className={overlayStyles.navigationButton}
                        >
                            <span className={overlayStyles.navigationItem}>
                                <span className={overlayStyles.navigationIcon}>
                                    {tab.icon}
                                </span>
                                {tab.label}
                                <TriangleIcon
                                    className={classNames(
                                        overlayStyles.navigationActiveIndicator,
                                        "icon-color-gray-500",
                                    )}
                                />
                            </span>
                        </FilterNavTab>
                    ))}
                    content={tabs.map((tab) => (
                        <FilterTabPanel
                            key={tab.key}
                            eventKey={tab.key}
                            className={styles.panel}
                        >
                            {tab.panel}
                        </FilterTabPanel>
                    ))}
                />
            </AggregatedFilters>
            <ActiveFilters
                activeFilters={activeKeys}
                renderVisibleFilters={(visibleKeys) =>
                    visibleKeys.flatMap((key) => {
                        const tab = tabsByKey.get(key);
                        return tab ? (
                            <ActivityFilterChip key={key} tab={tab} />
                        ) : (
                            []
                        );
                    })
                }
                clearFilters={noop}
                hideClearButton
                filtersContainerRef={filtersContainerRef}
            />
            {onClearFilters && <ClearFiltersButton onClick={onClearFilters} />}
        </AggregatedFiltersProvider>
    );
};
